import React from "react";
import { Typography } from "@mui/material";
import styled from "@emotion/styled";
import LegendSwatch from "@hyro-components/hyro-pie-chart/LegendSwatch";
import HyroTooltipSection from "@hyro-components/hyro-tooltip-section/HyroTooltipSection";
import TrendIndicator from "src/components/widgets/overview/tiles/TrendIndicator";
import { SANKEY_TOOLTIP_MIN_WIDTH } from "./HyroSankey.consts";

const SankeyTooltipContent = ({ color, label, headerValue, headerAdornment, value, caption, delta, sections = [] }) => (
  <Content data-testid="sankey-tooltip-content" compact={!value && !sections.length}>
    <HeaderRow>
      <HeaderLabelGroup>
        <LegendSwatch color={color} />
        <Typography variant="tooltip" color="text.800" sx={{ whiteSpace: "nowrap" }}>{label}</Typography>
      </HeaderLabelGroup>
      {(headerValue || headerAdornment) && (
        <HeaderValueGroup>
          {headerValue && <Typography variant="tooltip" color="text.700" sx={{ whiteSpace: "nowrap" }}>{headerValue}</Typography>}
          {headerAdornment}
        </HeaderValueGroup>
      )}
    </HeaderRow>
    {value && (
      <MetricRow>
        <MetricGroup>
          <Typography variant="h5" color="text.800">{value}</Typography>
          {caption && <Caption variant="tooltip" color="text.700">{caption}</Caption>}
        </MetricGroup>
        {delta && <TrendIndicator {...delta} showPeriod={false} />}
      </MetricRow>
    )}
    {sections.map((section, index) => <HyroTooltipSection key={`${section.title}-${index}`} {...section} />)}
  </Content>
);

// nivo renders its tooltips outside the popper Tooltip provides, so a chart tooltip
// has to carry its own surface.
export const ChartTooltipCard = styled("div")(({ theme }) => ({
  background: theme.palette.system[0],
  padding: theme.spacing(1.5, 1.75),
  borderRadius: theme.spacing(1),
  boxShadow: theme.shadows[4],
  boxSizing: "border-box"
}));

const Content = styled("div", { shouldForwardProp: (prop) => prop !== "compact" })(({ theme, compact }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: theme.spacing(1),
  ...(compact ? {} : { minWidth: SANKEY_TOOLTIP_MIN_WIDTH })
}));

const HeaderRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  width: "100%"
}));

const HeaderLabelGroup = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1)
}));

const HeaderValueGroup = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flexShrink: 0,
  whiteSpace: "nowrap"
}));

const MetricRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: theme.spacing(2)
}));

const MetricGroup = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  gap: theme.spacing(1)
}));

const Caption = styled(Typography)({
  whiteSpace: "nowrap",
  paddingBottom: 3
});

export default SankeyTooltipContent;
